import { formatISO9075 } from 'date-fns';
import {
  ACCOUNT_FLAG_VALUE,
  PRIVEE_STATUS,
  RawEPCCUser,
  USER_STATUS,
  User
} from '@/types/User';
import { SubscriptionProduct } from '@/types/Subscription';

export function mapEPCCUserToLocalUser(rawUser: RawEPCCUser): User {
  const user: Partial<User> = {};

  user.id = rawUser.id || '';
  user.email = rawUser.email || '';
  user.name = rawUser.name || '';
  user.status = rawUser.status || null;
  user.isAdmin = (rawUser.roles || [])?.includes('Admin');
  user.referralCode = rawUser.generated_referral_code;
  if (rawUser.referrer_code) user.referrerCode = rawUser.referrer_code;
  user.subscriptionOrderId = rawUser.subscription_order_id;
  user.paymentCustomerId = rawUser.stripe_customer_id;
  user.phone = rawUser.phone;
  user.isLocked = false;
  user.isPastDue = false;
  user.isFraud = false;
  user.isNewMember = false;
  user.priveeStatus = rawUser?.privee_status?.toLowerCase() as
    | PRIVEE_STATUS
    | undefined;
  user.isInterestedInPrivee = rawUser.interested_in_privee ?? false;
  if (rawUser.privee_acceptance_date)
    user.priveeAcceptanceDate = rawUser.privee_acceptance_date;
  if (rawUser.privee_frozen_date)
    user.priveeFrozenDate = rawUser.privee_frozen_date;
  user.isBrandAmbassador = rawUser.brand_ambassador;

  if (rawUser.first_name) user.firstName = rawUser.first_name;
  if (rawUser.last_name) user.lastName = rawUser.last_name;
  if (rawUser.birthday) user.birthday = rawUser.birthday;

  const socialMedia: Partial<User['socialMedia']> = {};
  if (rawUser.social_media_instagram) {
    socialMedia.instagram = rawUser.social_media_instagram;
  }

  if (rawUser.social_media_facebook) {
    socialMedia.facebook = rawUser.social_media_facebook;
  }

  if (rawUser.social_media_tiktok) {
    socialMedia.tiktok = rawUser.social_media_tiktok;
  }

  if (Object.keys(socialMedia).length) {
    user.socialMedia = socialMedia;
  }

  const subscriptions: Partial<User['subscriptions']> = {};
  if ('subscribe-wishlist-emails' in rawUser) {
    subscriptions.wishlist = rawUser['subscribe-wishlist-emails'];
  }
  if ('subscribed_sms' in rawUser) {
    subscriptions.sms = rawUser.subscribed_sms;
  }

  if ('marketing-emails-opt-in' in rawUser) {
    subscriptions.email = rawUser['marketing-emails-opt-in'];
  }

  user.subscriptions = subscriptions;

  // If user is locked, then spoof their status as Pending
  const accountFlags = rawUser.account_flag
    ?.split(',')
    .map((flag) => flag.trim()) as ACCOUNT_FLAG_VALUE[];
  if (accountFlags?.includes(ACCOUNT_FLAG_VALUE.ACCOUNT_LOCKED)) {
    user.status = USER_STATUS.PENDING;
    user.isLocked = true;
  }

  if (accountFlags?.includes(ACCOUNT_FLAG_VALUE.PAST_DUE)) {
    user.isPastDue = true;
  }

  if (accountFlags?.includes(ACCOUNT_FLAG_VALUE.FRAUD)) {
    user.isFraud = true;
  }

  if (accountFlags?.includes(ACCOUNT_FLAG_VALUE.NEW_MEMBER)) {
    user.isNewMember = true;
  }

  return user as User;
}

/**
 *
 * @param string Date in format 'mm/dd/YYYY'
 * @returns
 */
export function formatEPCCBirthDate(date: string): string {
  // Entry format mm/dd/YYYY

  const [month, day, year] = date
    .split('/')
    .map((datePart) => parseInt(datePart, 10));

  // EPCC wants dates in format yyyy-mm-dd hh:mm:ss
  // Subtract 1 from month because date object wants it zero-based
  return formatISO9075(new Date(year, month - 1, day, 0, 0, 0));
}

/**
 * Returns the tier of the customer based on their subscription plan
 * @param subscriptionPlan The subscription plan of the customer
 * @returns The tier of the customer
 */
export function getCustomerTier(
  subscriptionPlan: SubscriptionProduct[]
): string {
  return subscriptionPlan[0]?.name ?? '';
}

/**
 * Returns true if the customer has a Premier add-on
 * @param subscriptionPlan The subscription plan of the customer
 * @returns True if the customer has a Premier add-on
 */
export function customerHasPremierAddOn(
  subscriptionPlan: SubscriptionProduct[]
): boolean {
  return !!subscriptionPlan?.[1]?.name;
}
